'use client';

import { useState } from 'react';
import {
  Box,
  Brain,
  Lightbulb,
  Rocket,
  Ruler,
  Sparkles,
  Target,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import type { PaywallBullet } from '@/shared/lib/api';

/**
 * Maps a server-driven semantic bullet `icon` key to a local lucide icon.
 * The server never sends asset URLs (the bullet carries `image_url`
 * separately for that) — only these stable keys — so a renamed or unknown
 * key degrades to a neutral Sparkles glyph rather than a broken image. Keep
 * in sync with the API enum and Apple/Android bullet maps.
 *
 * `target` (progress goal — also superseded by the user's last-render
 * thumbnail via `image_url` when available) + `lightbulb` ("new ideas", the
 * iterate-bullet semantic) are the active INSUFFICIENT_TOKENS bullet icons.
 * The older `sparkles / rocket / angle / architect / blueprint` keys stay
 * mapped for any cached payload still using them — `sparkles` predated
 * `lightbulb` on the iterate bullet.
 */
const ICON_MAP: Record<string, LucideIcon> = {
  target: Target,
  lightbulb: Lightbulb,
  rocket: Rocket,
  sparkles: Sparkles,
  angle: Box,
  architect: Brain,
  blueprint: Ruler,
};

/**
 * Server-driven benefit bullets shown above the token packs on a paywall.
 * Copy is fully server-localized; this component only renders icon + label.
 *
 * Layout: vertical list (one row per bullet, icon left + label right). The
 * earlier grid/wrap layout (tiles side-by-side) was hard to skim on the
 * conversion path — bullets need to be READ for the paywall to do its job,
 * so the list form trades footprint for legibility. Mirrors Apple/Android,
 * which both stack vertically too. Neutral grey icons, no chip background.
 *
 * Renders nothing when `bullets` is absent/empty (most paywalls carry none).
 *
 * `centered`: shrink the list to its content width and center it horizontally
 * (still left-flagged — icons stay in one left column). Used on the Tokens
 * page so the block sits centered on wide screens instead of stranded at the
 * far-left edge; sheets/dialogs keep the default full-width left layout.
 */
export function PaywallBullets({
  bullets,
  className,
  centered = false,
}: {
  bullets: PaywallBullet[] | undefined;
  className?: string;
  centered?: boolean;
}) {
  if (!bullets || bullets.length === 0) return null;

  return (
    <ul
      className={cn(
        'flex flex-col gap-2',
        centered ? 'mx-auto w-fit' : 'w-full',
        className
      )}
    >
      {bullets.map((bullet, index) => (
        <li
          key={`${bullet.icon}-${index}`}
          className="flex w-full items-center gap-3"
        >
          <BulletLeading bullet={bullet} />
          <span className="text-card-body text-muted-foreground">
            {bullet.label}
          </span>
        </li>
      ))}
    </ul>
  );
}

/**
 * Leading glyph for one bullet — either a tiny circular thumbnail of the
 * user's last render (when the server attached `image_url`) or the Lucide
 * icon mapped from `bullet.icon`. The icon path is also the fallback on
 * image-load failure so the row never goes blank. Fixed 20px slot on both
 * branches keeps every label aligned at the same x-offset regardless of
 * which leading shape this bullet uses.
 */
function BulletLeading({ bullet }: { bullet: PaywallBullet }) {
  const Icon = ICON_MAP[bullet.icon] ?? Sparkles;
  const [imageFailed, setImageFailed] = useState(false);

  if (bullet.image_url && !imageFailed) {
    return (
      // Raw <img> is fine for a tiny user-uploaded thumbnail without
      // next/image's configured-domains overhead; URLs come from the API and
      // aren't known ahead of build time.
      <img
        src={bullet.image_url}
        alt=""
        className="h-5 w-5 shrink-0 rounded-full object-cover"
        onError={() => setImageFailed(true)}
      />
    );
  }
  // All bullet icons stay neutral dark-grey — no per-icon accent colouring.
  return <Icon className="h-5 w-5 shrink-0 text-muted-foreground" />;
}
